import * as React from 'react';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { Check, ChevronDown, MapPin, Search, Warehouse } from 'lucide-react';
import { useContexto } from '@/hooks/useContexto';
import { Esqueleto } from '@/componentes/ui/primitivos';
import { cn } from '@/lib/utils';

/**
 * Contexto fixo no header (regra 5), persistido em localStorage e refletido
 * na URL.
 *
 * Um botão que abre um painel com busca: quem tem 2 centros troca em um
 * clique, quem tem 40 digita. O tipo de estoque continua sendo rótulo, não
 * campo: /api/contexto devolve um tipo por centro, então escolher centro já
 * escolhe o tipo.
 *
 * A lista aqui é só da EMPRESA selecionada no seletor ao lado. Antes eram
 * todos os centros agrupados por empresa, o que virava uma rolagem longa para
 * quem enxerga muitas empresas — e pior, deixava trocar de empresa sem
 * perceber, só escolhendo um centro de outro grupo.
 */
export function SeletorContexto(): JSX.Element {
  const { centrosDaEmpresa, centro, centroAtual, definirCentro, carregando } = useContexto();
  const [aberto, definirAberto] = React.useState(false);
  const [termo, definirTermo] = React.useState('');

  const filtrados = React.useMemo(() => {
    const t = termo.trim().toLowerCase();
    if (!t) return centrosDaEmpresa;
    return centrosDaEmpresa.filter(
      (c) => c.nome.toLowerCase().includes(t) || String(c.centroEstoque).includes(t),
    );
  }, [centrosDaEmpresa, termo]);

  if (carregando) return <Esqueleto className="h-11 w-56" />;

  if (centrosDaEmpresa.length === 0 || centro === null) {
    return (
      <span className="text-[13px] text-textoFraco">Nenhum centro liberado para o seu usuário.</span>
    );
  }

  return (
    <DropdownMenu.Root
      open={aberto}
      onOpenChange={(v) => {
        definirAberto(v);
        if (!v) definirTermo('');
      }}
    >
      <DropdownMenu.Trigger asChild>
        <button
          type="button"
          aria-label="Trocar centro de estoque"
          className={cn(
            'group flex min-h-[42px] max-w-[280px] items-center gap-2.5 rounded-xl border border-borda bg-superficie px-2.5 pr-3 shadow-xs transition-all',
            'hover:border-bordaForte hover:shadow-cartao data-[state=open]:border-primaria data-[state=open]:ring-4 data-[state=open]:ring-primaria/10',
          )}
        >
          <span className="grid h-7 w-7 shrink-0 place-items-center rounded-lg bg-primaria-suave text-primaria">
            <Warehouse className="h-4 w-4" />
          </span>
          <span className="min-w-0 text-left leading-tight">
            <span className="block text-[9.5px] font-bold uppercase tracking-[0.1em] text-textoSuave">
              Centro
            </span>
            <span className="block truncate text-[13px] font-semibold text-texto">
              {centroAtual ? `${centroAtual.centroEstoque} · ${centroAtual.nome}` : centro}
            </span>
          </span>
          <ChevronDown className="h-4 w-4 shrink-0 text-textoSuave transition-transform group-data-[state=open]:rotate-180" />
        </button>
      </DropdownMenu.Trigger>

      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="start"
          sideOffset={8}
          className="z-50 w-[min(380px,calc(100vw-24px))] overflow-hidden rounded-2xl border border-borda bg-superficie shadow-forte data-[state=open]:animate-escalar"
        >
          <div className="flex items-center gap-2 border-b border-borda px-3">
            <Search className="h-4 w-4 shrink-0 text-textoSuave" />
            <input
              autoFocus
              value={termo}
              onChange={(e) => definirTermo(e.target.value)}
              onKeyDown={(e) => e.stopPropagation()}
              placeholder="Buscar centro…"
              aria-label="Buscar centro"
              className="min-h-[46px] w-full bg-transparent text-[13.5px] outline-none placeholder:text-textoSuave"
            />
          </div>

          <div className="max-h-[52vh] overflow-y-auto p-1.5">
            {filtrados.length === 0 ? (
              <p className="px-3 py-6 text-center text-[13px] text-textoFraco">
                Nenhum centro com “{termo}”.
              </p>
            ) : (
              filtrados.map((c) => {
                const ativo = c.centroEstoque === centro;
                return (
                  <button
                    key={c.centroEstoque}
                    type="button"
                    onClick={() => {
                      definirCentro(c.centroEstoque);
                      definirAberto(false);
                    }}
                    className={cn(
                      'flex w-full items-center gap-3 rounded-lg px-2.5 py-2 text-left transition-colors',
                      ativo ? 'bg-primaria-suave' : 'hover:bg-superficie2',
                    )}
                  >
                    <span
                      className={cn(
                        'tabular grid h-9 w-11 shrink-0 place-items-center rounded-lg text-[12px] font-bold',
                        ativo
                          ? 'gradiente-marca text-white'
                          : 'bg-superficie2 text-textoFraco ring-1 ring-inset ring-borda',
                      )}
                    >
                      {c.centroEstoque}
                    </span>
                    <span className="min-w-0 flex-1 leading-tight">
                      <span
                        className={cn(
                          'block truncate text-[13px] font-semibold',
                          ativo && 'text-primaria',
                        )}
                      >
                        {c.nome}
                      </span>
                      <span className="flex items-center gap-1 text-[11px] text-textoFraco">
                        tipo {c.tipoEstoque}
                        {c.controlaLocalizador ? (
                          <>
                            <span aria-hidden>·</span>
                            <MapPin className="h-2.5 w-2.5" />
                            endereçado
                          </>
                        ) : (
                          <>
                            <span aria-hidden>·</span>sem endereçamento
                          </>
                        )}
                      </span>
                    </span>
                    {ativo ? <Check className="h-4 w-4 shrink-0 text-primaria" /> : null}
                  </button>
                );
              })
            )}
          </div>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}
